import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { matchesUA  } from 'browserslist-useragent';
import {
	convertSeconds, useClassNames,
} from '@common';

import {
	ProgressBar,
	IconButton,
	Volume,
} from '@components';

import {
	PlayerPlayIcon,
	PlayerPauseIcon,
} from '@icons';

// LATER: ЧТО-ТО С ЭТИМ СДЕЛАТЬ!!!
import { PlayerSettings } from '../../meeting/player-box/default-settings';

import './player.scss';

export interface PlayerProps {
	playerRef: React.MutableRefObject<HTMLAudioElement>,
	mediaUrl: string,
	onIsPlayingChanged: (isPlaying: boolean) => void,
	onTimeChanged: (seconds: number) => void,
	showHoverProgress?: boolean,
	defaultStartTimeTicks?: number,
	defaultEndTimeTicks?: number,
	disabled?: boolean,
	onPlayerLoaded?: () => void,
}

function Player(props: PlayerProps): JSX.Element {
	const {
		showHoverProgress = true,
		onIsPlayingChanged,
		onTimeChanged,
		playerRef,
		mediaUrl,
		defaultStartTimeTicks,
		defaultEndTimeTicks,
		disabled,
		onPlayerLoaded,
	} = props;

	const isSafari = matchesUA(
		navigator.userAgent,
		{ browsers: ['Safari > 1.0'] }
	);

	// state
	const
		[playerHoverTime, setPlayerHoverTime] = useState<number>(0),
		[volume, setVolume] = useState<number>(PlayerSettings.defaultVolume),
		[playerDuration, setPlayerDuration] = useState<number>(0),
		[playerTime, setPlayerTime] = useState<number>(0),
		[isPlaying, setIsPlaying] = useState<boolean>(false),
		[disabledPlayback, setDisabledPlayback] = useState<boolean>(!isSafari);


	// methods
	const onPlayButtonClick = useCallback((): void => {
		if (playerRef.current.paused) {
			playerRef.current.play();
		} else {
			playerRef.current.pause();
		}

		setIsPlaying(!playerRef.current.paused);
	}, [setIsPlaying, playerRef]);

	const onPause = useCallback((): void => {
		setIsPlaying(false);
		onIsPlayingChanged(false);
	}, [setIsPlaying, onIsPlayingChanged]);

	const onPlay = useCallback((): void => {
		setIsPlaying(true);
		onIsPlayingChanged(true);
	}, [setIsPlaying, onIsPlayingChanged]);

	const onDurationChange = useCallback((): void => {
		setPlayerDuration(playerRef.current.duration);
	}, [setPlayerDuration, playerRef]);

	const onPlayerHoverTimeChange = useCallback((newHoverTimeProgress: number) => {
		if (disabledPlayback) {
			return;
		}
		setPlayerHoverTime(newHoverTimeProgress / 100 * playerRef.current.duration);
	}, [setPlayerHoverTime, playerRef, disabledPlayback]);

	const onPlayerVolumeChange = useCallback((newVolumeProgress: number) => {
		const newVolume = newVolumeProgress / 100;
		playerRef.current.volume = newVolume;
		setVolume(newVolume);
	}, [setVolume, playerRef]);

	const onPlayerTimeChange = useCallback((newCurrentTime: number): void => {
		if (disabledPlayback) {
			return;
		}
		setPlayerTime(newCurrentTime);
		onTimeChanged(newCurrentTime);
	}, [onTimeChanged, disabledPlayback]);

	const onAudioTimeUpdate = useCallback((e) => {
		onPlayerTimeChange(e.target.currentTime);
	}, [onPlayerTimeChange]);

	const onProgressBarTimeChange = useCallback((newPlayerTimeProgress: number) => {
		const newCurrentTime = playerRef.current.duration != Infinity
			? newPlayerTimeProgress / 100 * playerRef.current.duration
			: 0;
		
		playerRef.current.currentTime = newCurrentTime;
		onPlayerTimeChange(newCurrentTime);
	}, [onPlayerTimeChange, playerRef]);

	const onLoad = useCallback(() => {
		if (!disabled) {
			setDisabledPlayback(false);
			onPlayerLoaded && onPlayerLoaded();
		}
	}, [disabled, onPlayerLoaded]);

	
	useEffect(() => {
		if (defaultStartTimeTicks) {
			const totalMilliseconds = defaultStartTimeTicks / 10000;
			const defaultStartTimeSeconds =  totalMilliseconds / 1000;
			
			playerRef.current.currentTime = defaultStartTimeSeconds;
			onPlayerTimeChange(defaultStartTimeSeconds);
		}
	}, [defaultStartTimeTicks, onPlayerTimeChange, playerRef]);
	
	const timeCodeStart = useMemo((): number => {
		if (defaultStartTimeTicks) {
			const totalMilliseconds = defaultStartTimeTicks / 10000;
			const totalSeconds =  totalMilliseconds / 1000;

			return 100 * totalSeconds / playerDuration
		} else {
			return null;
		}
	}, [defaultStartTimeTicks, playerDuration]);

	const timeCodeEnd = useMemo((): number => {
		if (defaultEndTimeTicks) {
			const totalMilliseconds = defaultEndTimeTicks / 10000;
			const totalSeconds =  totalMilliseconds / 1000;

			return 100 * totalSeconds / playerDuration
		} else {
			return null;
		}
	}, [defaultEndTimeTicks, playerDuration]);

	// computed
	const progress = useMemo((): number => {
		return 100 * playerTime / playerDuration;
	}, [playerTime, playerDuration]);

	const progressText = useMemo((): string => {
		return convertSeconds(playerTime);
	}, [playerTime]);

	const hoverProgress = useMemo((): number => {
		return 100 * playerHoverTime / playerDuration;
	}, [playerHoverTime, playerDuration]);

	const hoverProgressText = useMemo((): string => {
		return convertSeconds(playerHoverTime);
	}, [playerHoverTime]);

	const playerTimeStatusText = useMemo((): string => {
		const
			convertedPlayerTimeText = convertSeconds(playerTime),
			convertedPlayerDurationText = convertSeconds(playerDuration);

		return `${convertedPlayerTimeText} / ${convertedPlayerDurationText}`;
	}, [playerTime, playerDuration]);

	const classNames = useClassNames(
		'player',
		disabled && 'disabled'
	);

	useEffect(() => {
		if (disabled) {
			setPlayerDuration(0);
			setDisabledPlayback(!isSafari);
			setIsPlaying(false);
			setPlayerTime(0);
			setPlayerHoverTime(0);
		}
	}, [disabled, isSafari]);

	return (
		<div className={classNames}>
			<div className="player__controls">
				<IconButton
					disabled={disabledPlayback}
					onClick={onPlayButtonClick}
					icon={isPlaying ? <PlayerPauseIcon /> : <PlayerPlayIcon />}
					size="nogap"
					iconSize="30px"
				/>
				<Volume
					disabled={disabledPlayback}
					onVolumeChange={onPlayerVolumeChange}
					volume={volume}
				/>
			</div>
			<span className="player__duration">
				{playerTimeStatusText}
			</span>
			<ProgressBar
				disabled={disabledPlayback}
				onHoverProgressChange={onPlayerHoverTimeChange}
				onProgressChange={onProgressBarTimeChange}
				hoverProgressText={hoverProgressText}
				showHoverProgress={showHoverProgress}
				hoverProgress={hoverProgress}
				progressText={progressText}
				progress={progress}
				timecodeStart={timeCodeStart}
				timecodeEnd={timeCodeEnd}
			/>
			<audio
				onDurationChange={onDurationChange}
				onTimeUpdate={onAudioTimeUpdate}
				onPause={onPause}
				onPlay={onPlay}
				onCanPlay={onLoad}
				ref={playerRef}
				preload="metadata"
			>
				{mediaUrl && <source src={mediaUrl} type="audio/mp4" />}
			</audio>
		</div>
	);
}

export {Player};